/**
 * Preset-uri pentru stilul header-ului tabelelor (`.v-table thead`,
 * `.v-data-table-skin thead`). User-ul alege din `/setari?tab=appearance` →
 * setarea persistă în `theme-prefs-store` și se aplică instant pe toate
 * tabelele prin CSS variables `--v-th-*`.
 *
 * Toate preset-urile respectă WCAG AA contrast minim. Combinațiile sunt
 * curated pentru a evita „cream pe alb" sau alte combinații nesigure.
 *
 * DARK MODE: preset-urile deschise NU au o a doua tabelă „dark" în JS — valorile
 * trec prin tokenii de temă (`--v-surface`, `--v-text`, `--v-th-tint-*`, …),
 * definiți light în `:root` și dark în `html.dark` (vanzari.css). Așa header-ul
 * urmează tema fără detecție în JS și fără flash la hydration. Hex-ul din `var(…)`
 * rămâne doar ca fallback dacă tokenul lipsește.
 * ⚠ Ține în sincron cu mirror-ul pre-hydration `THP` din app/layout.tsx
 * (testul din table-header-styles.spec.ts verifică asta).
 */

export type TableHeaderStyle =
  | 'dark-slate'      // V8 default — high contrast slate gradient + cream
  | 'dark-primary'    // Brand-coordinated dark — primary-900 + white
  | 'light-muted'     // Pre-V8 classic — cream-deep cu text muted
  | 'light-bordered'  // Editorial Stripe-style — alb cu 2px primary border
  | 'tinted-soft'     // Branded subtle — primary-50 bg + primary-900 text
  | 'monochrome';     // Brutalist — pur negru #1A1A1A + alb pur

export interface TableHeaderPreset {
  id: TableHeaderStyle;
  name: string;
  desc: string;
  cssVars: {
    '--v-th-bg': string;
    '--v-th-bg-hover': string;
    '--v-th-text': string;
    '--v-th-text-muted': string;
    '--v-th-sort-arrow': string;
    '--v-th-border': string;
    '--v-th-divider': string;
    '--v-th-checkbox-accent': string;
  };
}

export const DEFAULT_TABLE_HEADER_STYLE: TableHeaderStyle = 'light-bordered';

export const TABLE_HEADER_PRESETS: Record<TableHeaderStyle, TableHeaderPreset> = {
  'dark-slate': {
    id: 'dark-slate',
    name: 'Slate',
    desc: 'Header dark cu text cream — high contrast, ancorat vizual (default).',
    cssVars: {
      '--v-th-bg': 'linear-gradient(180deg, #2A2724 0%, #1F1D18 100%)',
      '--v-th-bg-hover': 'linear-gradient(180deg, #34302A 0%, #28251F 100%)',
      '--v-th-text': '#FBF9F4',
      '--v-th-text-muted': 'rgba(251,249,244,0.55)',
      '--v-th-sort-arrow': 'rgb(var(--primary-300))',
      '--v-th-border': 'rgb(var(--primary-500))',
      '--v-th-divider': 'rgba(251,249,244,0.05)',
      '--v-th-checkbox-accent': 'rgb(var(--primary-500))',
    },
  },
  'dark-primary': {
    id: 'dark-primary',
    name: 'Brand dark',
    desc: 'Fundal în culoarea principală (primary-900) cu text alb. Branding consistent.',
    cssVars: {
      '--v-th-bg': 'linear-gradient(180deg, rgb(var(--primary-800)) 0%, rgb(var(--primary-900)) 100%)',
      '--v-th-bg-hover': 'linear-gradient(180deg, rgb(var(--primary-700)) 0%, rgb(var(--primary-800)) 100%)',
      '--v-th-text': '#FFFFFF',
      '--v-th-text-muted': 'rgba(255,255,255,0.60)',
      '--v-th-sort-arrow': 'rgb(var(--primary-200))',
      '--v-th-border': 'rgb(var(--primary-300))',
      '--v-th-divider': 'rgba(255,255,255,0.08)',
      '--v-th-checkbox-accent': '#FFFFFF',
    },
  },
  'light-muted': {
    id: 'light-muted',
    name: 'Cream subtle',
    desc: 'Fundal cream-deep cu text muted — discret, neagresiv (stil clasic pre-V8).',
    cssVars: {
      '--v-th-bg': 'var(--v-bg-deeper, #F4F4F3)',
      '--v-th-bg-hover': 'var(--v-border, #EDEAE3)',
      '--v-th-text': 'var(--v-text-muted, #5F5D58)',
      '--v-th-text-muted': 'var(--v-text-faint, #8E8B83)',
      '--v-th-sort-arrow': 'var(--v-th-accent, rgb(var(--primary-500)))',
      '--v-th-border': 'var(--v-border, #EDEAE3)',
      '--v-th-divider': 'var(--v-hairline, rgba(24,23,26,0.04))',
      '--v-th-checkbox-accent': 'var(--v-th-accent, rgb(var(--primary-500)))',
    },
  },
  'light-bordered': {
    id: 'light-bordered',
    name: 'Alb cu bordură',
    desc: 'Fundal alb pur cu text negru și bordură 2px primary. Editorial, ca Stripe Dashboard.',
    cssVars: {
      '--v-th-bg': 'var(--v-surface, #FFFFFF)',
      '--v-th-bg-hover': 'var(--v-bg-deeper, #F4F4F3)',
      '--v-th-text': 'var(--v-text, #18171A)',
      '--v-th-text-muted': 'var(--v-text-muted, #5F5D58)',
      '--v-th-sort-arrow': 'var(--v-th-accent, rgb(var(--primary-500)))',
      '--v-th-border': 'rgb(var(--primary-500))',
      '--v-th-divider': 'var(--v-hairline, rgba(24,23,26,0.05))',
      '--v-th-checkbox-accent': 'var(--v-th-accent, rgb(var(--primary-500)))',
    },
  },
  'tinted-soft': {
    id: 'tinted-soft',
    name: 'Tint primary',
    desc: 'Fundal foarte deschis cu nuanță din culoarea principală. Branded subtle, harmonios.',
    cssVars: {
      '--v-th-bg': 'var(--v-th-tint-bg, rgb(var(--primary-50)))',
      '--v-th-bg-hover': 'var(--v-th-tint-bg-hover, rgb(var(--primary-100)))',
      '--v-th-text': 'var(--v-th-tint-text, rgb(var(--primary-900)))',
      '--v-th-text-muted': 'var(--v-th-tint-text-muted, rgb(var(--primary-700)))',
      '--v-th-sort-arrow': 'var(--v-th-tint-arrow, rgb(var(--primary-600)))',
      '--v-th-border': 'var(--v-th-tint-border, rgb(var(--primary-300)))',
      '--v-th-divider': 'var(--v-th-tint-divider, rgb(var(--primary-200) / 0.5))',
      '--v-th-checkbox-accent': 'var(--v-th-tint-arrow, rgb(var(--primary-600)))',
    },
  },
  'monochrome': {
    id: 'monochrome',
    name: 'Monochrome',
    desc: 'Pur negru cu text alb pur. Brutalist, fără nuanțe warm.',
    cssVars: {
      '--v-th-bg': '#1A1A1A',
      '--v-th-bg-hover': '#2A2A2A',
      '--v-th-text': '#FFFFFF',
      '--v-th-text-muted': 'rgba(255,255,255,0.55)',
      '--v-th-sort-arrow': '#FFFFFF',
      '--v-th-border': 'var(--v-th-mono-border, #000000)',
      '--v-th-divider': 'rgba(255,255,255,0.08)',
      '--v-th-checkbox-accent': '#FFFFFF',
    },
  },
};

export const TABLE_HEADER_STYLE_OPTIONS: TableHeaderPreset[] = Object.values(TABLE_HEADER_PRESETS);

export type TableHeaderCssVars = TableHeaderPreset['cssVars'];

/**
 * Variabilele `--v-th-*` de aplicat pentru un preset (identice în ambele teme —
 * adaptarea la dark o fac tokenii, vezi comentariul de sus).
 * Preset-urile necunoscute cad pe `light-bordered` (default-ul aplicației).
 */
export function resolveTableHeaderVars(style: string | null | undefined): TableHeaderCssVars {
  const key = (style && style in TABLE_HEADER_PRESETS ? style : 'light-bordered') as TableHeaderStyle;
  return TABLE_HEADER_PRESETS[key].cssVars;
}
